import { PickerFlushReason, type MarkOutcome, type Span } from '@wiztime/core';
import { create } from 'zustand';
import { removeStorage } from '../../lib/safeStorage';
import { sameSpan } from '../../lib/span';
import { LEGACY_QUICK_MARK_PROJECT_KEY } from '../../lib/storageKeys';
import { flushPicker } from './picker/pickerFlush';
import {
  baselineTextOf,
  initPickerFields,
  type PickerBaseline,
  type PickerFields,
  type PickerTextField,
} from './picker/pickerModel';
import { GestureKind, useGestureCounts } from './gestureCount';
import {
  activateQuickMark,
  lockQuickMark,
  QUICK_MARK_OFF,
  QuickMarkMode,
  settleQuickMark,
  type QuickMarkSource,
  type QuickMarkState,
} from './quickMark';

/** A rectangle in viewport coordinates a floating panel is anchored to. */
export interface AnchorRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export const PickerKind = { Create: 'create', Edit: 'edit' } as const;
export type PickerKind = (typeof PickerKind)[keyof typeof PickerKind];

/** Where the picker puts its initial focus (2026-10b §5.3): the project list by default. */
export const PickerFocus = { List: 'list', Title: 'title', Start: 'start' } as const;
export type PickerFocus = (typeof PickerFocus)[keyof typeof PickerFocus];

/** The project picker is open for a not yet created block, or for an existing block group. */
export type PickerState =
  | {
      readonly kind: typeof PickerKind.Create;
      readonly date: string;
      readonly span: Span;
      readonly anchor: AnchorRect;
      readonly focus?: PickerFocus;
      /** Restored from the page-hide stash (2026-10b §5.4): pre-filled title and note. */
      readonly restored?: { readonly title: string; readonly note: string };
    }
  | {
      readonly kind: typeof PickerKind.Edit;
      readonly date: string;
      readonly groupKey: string;
      readonly anchor: AnchorRect;
      readonly focus?: PickerFocus;
    };

/** The overlap popover is open for this block (ui-redesign §3.8); its rows are derived live. */
export interface ConflictState {
  readonly date: string;
  /** Id of the block the popover is about ("this block" of its actions). */
  readonly id: string;
  readonly anchor: AnchorRect;
}

/**
 * The block context menu is open (2026-10b §6.3). `x`/`y` is where the menu's top-left corner
 * wants to sit in viewport coordinates: 2px right of and below the pointer, or 12px inside the
 * block's top-left corner when opened by the keyboard.
 */
export const ContextMenuSource = { Pointer: 'pointer', Keyboard: 'keyboard' } as const;
export type ContextMenuSource = (typeof ContextMenuSource)[keyof typeof ContextMenuSource];

/** A pointer `contextmenu` this soon after a keyboard-opened menu is the key press's echo. */
export const CONTEXT_MENU_ECHO_MS = 400;

export interface ContextMenuState {
  readonly date: string;
  /** Key of the block group the menu acts on (the whole merged group). */
  readonly groupKey: string;
  readonly x: number;
  readonly y: number;
  readonly source: ContextMenuSource;
  /** `Date.now()` at opening: a keyboard-originated native `contextmenu` right after is ignored. */
  readonly openedAt: number;
}

/** Asks the lane that draws block `blockId` to move keyboard focus onto it once it is on screen. */
export interface FocusRequest {
  readonly id: number;
  readonly blockId: string;
}

/** A focus request that finds no block this long after it was made (the write failed) is dropped. */
export const FOCUS_REQUEST_MS = 3_000;

export interface TileDragPreview {
  readonly date: string;
  readonly span: Span;
  /** False while a drop on this lane would write nothing (lane not ready, overlay open, write in flight). */
  readonly allowed: boolean;
}

/**
 * What a quick-mark tile needs to know about the selected block group (design 2026-10-15 §1):
 * published by the day and week views from their loaded groups, so the tiles (which live
 * outside the day data context) can add a parallel block beside the selection.
 */
export interface SelectedGroupInfo {
  readonly key: string;
  readonly date: string;
  readonly span: Span;
  readonly projectId: string;
  /**
   * True when the user selected the block (click, opening its picker, context menu, review of
   * drafts); false when the selection was set automatically after a create (design 2026-10-15
   * §1.2): only an explicit selection makes a tile click add a parallel block.
   */
  readonly explicit: boolean;
}

interface DayUiState {
  /** Key (first member id) of the selected block group: target of Del and the arrow keys. */
  readonly selectedKey: string | null;
  /** Whether the user made the current selection (see `SelectedGroupInfo.explicit`). */
  readonly selectedExplicit: boolean;
  /** The selected group resolved against the loaded day data; null when none is selected or it is not loaded. */
  readonly selectedGroup: SelectedGroupInfo | null;
  readonly picker: PickerState | null;
  /**
   * Field drafts of the open picker (2026-10b §5.4): they live here, not in component state,
   * so the single commit point `flushPicker` reaches them on every close path.
   */
  readonly pickerFields: PickerFields | null;
  readonly conflict: ConflictState | null;
  /** The block context menu (2026-10b §6); at most one overlay of picker, popover and menu is open. */
  readonly contextMenu: ContextMenuState | null;
  /** Quick mark as a format brush (2026-10b §4.1): memory only, a reload starts at Off. */
  readonly quickMark: QuickMarkState;
  readonly focusRequest: FocusRequest | null;
  /** Span of the drag in progress (create, move or resize): drives the axis pills and guide lines. */
  readonly dragSpan: Span | null;
  /**
   * Drop preview of a quick-mark tile being dragged onto the timeline (2026-10c §2): the lane of
   * `date` draws the ghost. Null while no tile drag is in progress or no lane is under the pointer.
   */
  readonly tileDrag: TileDragPreview | null;
  /** Start minute of the hover ghost: drives the faint pill on the time axis. */
  readonly hoverMin: number | null;
  /** The gesture help overlay ("?" button or key, ui-redesign §3.6.3) is open. */
  readonly helpOpen: boolean;
  /** The user selects a block group (or clears the selection with null): an explicit selection. */
  select(key: string | null): void;
  /**
   * Select the group a command just created (design 2026-10-15 §1.2): an automatic selection.
   * Re-selecting the group that is already selected keeps whether the user selected it.
   */
  selectAuto(key: string): void;
  setSelectedGroup(group: SelectedGroupInfo | null): void;
  openPicker(picker: PickerState): void;
  closePicker(): void;
  /** Close without the Dismiss commit: Delete and Expire paths, and after an explicit commit. */
  closePickerDiscarding(): void;
  /** Install the field baselines once the picker host resolved its target (2026-10b §5.4). */
  setPickerBaseline(baseline: PickerBaseline): void;
  setPickerText(field: PickerTextField, value: string): void;
  /** Esc inside a modified input: back to the baseline value, picker stays open (§5.4). */
  resetPickerText(field: PickerTextField): void;
  /** After a commit: the committed values become the new baselines; the texts stay. */
  rebaselinePicker(baseline: PickerBaseline): void;
  /** Create context: show the "nothing saved yet" notice; the next dismissal abandons. */
  showPickerNotice(): void;
  openConflict(conflict: ConflictState): void;
  closeConflict(): void;
  /**
   * Open the block context menu on a block group (§6.3): the group becomes the selection, an
   * open picker is committed first (`flushPicker`, §5.4). Returns false, opening nothing, when
   * the commit vetoes (a dirty create context shows its first warning instead).
   */
  openContextMenu(menu: Omit<ContextMenuState, 'openedAt'>): boolean;
  closeContextMenu(): void;
  /** A tile was activated (§4.2); `detail` is `MouseEvent.detail` for pointer activations. */
  quickMarkActivate(projectId: string, source: QuickMarkSource, detail?: number): void;
  /** Lock directly: Shift+digit, Shift+Enter/Space, or (`byGesture`) the touch long press. */
  quickMarkLock(projectId: string, byGesture?: boolean): void;
  /** Esc, the banner's exit button, leaving the day/week views, or the project disappearing. */
  quickMarkExit(): void;
  /**
   * A marking command finished: consumes the "once" on `Created`/`Changed` (§4.3). The
   * "once" belongs to the action that produced the result: `startedFrom` is the quick-mark
   * state captured when the command started, and a late outcome arriving after the state
   * moved on (another tile picked while the write was in flight) is dropped.
   */
  quickMarkSettle(outcome: MarkOutcome, startedFrom: QuickMarkState): void;
  setDragSpan(span: Span | null): void;
  setTileDrag(preview: TileDragPreview | null): void;
  setHoverMin(min: number | null): void;
  requestFocus(blockId: string): void;
  clearFocusRequest(id: number): void;
  /** Close both overlays; `silent` suppresses the "saved" toast (route changes, §5.4). */
  closeOverlays(options?: { readonly silent?: boolean }): void;
  openHelp(): void;
  closeHelp(): void;
  toggleHelp(): void;
}

let nextFocusRequestId = 1;

// The quick-mark project is no longer persisted (2026-10b §4.1, data dictionary §7): clear
// the key older versions of this device may have left behind.
removeStorage(LEGACY_QUICK_MARK_PROJECT_KEY);

export const useDayUi = create<DayUiState>()((set, get) => ({
  selectedKey: null,
  selectedExplicit: false,
  selectedGroup: null,
  picker: null,
  pickerFields: null,
  conflict: null,
  contextMenu: null,
  quickMark: QUICK_MARK_OFF,
  focusRequest: null,
  dragSpan: null,
  tileDrag: null,
  hoverMin: null,
  helpOpen: false,
  select: (selectedKey) => set({ selectedKey, selectedExplicit: selectedKey !== null }),
  selectAuto: (selectedKey) => {
    const { selectedKey: current, selectedExplicit } = get();
    set({ selectedKey, selectedExplicit: current === selectedKey && selectedExplicit });
  },
  setSelectedGroup: (group) => {
    const current = get().selectedGroup;
    if (
      current === group ||
      (current !== null &&
        group !== null &&
        current.key === group.key &&
        current.date === group.date &&
        current.projectId === group.projectId &&
        current.explicit === group.explicit &&
        sameSpan(current.span, group.span))
    ) {
      return;
    }
    set({ selectedGroup: group });
  },
  openPicker: (picker) => {
    // Replacing one picker by another is a close path: commit first (2026-10b §5.4, path 2).
    // A veto (dirty create context, first warning) consumes the replacement.
    if (get().picker !== null && !flushPicker(PickerFlushReason.Dismiss, { silent: true })) return;
    set({ picker, pickerFields: null, conflict: null, contextMenu: null });
  },
  closePicker: () => {
    if (get().picker !== null && !flushPicker(PickerFlushReason.Dismiss)) return;
    set({ picker: null, pickerFields: null });
  },
  closePickerDiscarding: () => set({ picker: null, pickerFields: null }),
  setPickerBaseline: (baseline) => set({ pickerFields: initPickerFields(baseline) }),
  setPickerText: (field, value) => {
    const fields = get().pickerFields;
    if (fields === null || fields[field] === value) return;
    set({ pickerFields: { ...fields, [field]: value } });
  },
  resetPickerText: (field) => {
    const fields = get().pickerFields;
    if (fields === null) return;
    set({ pickerFields: { ...fields, [field]: baselineTextOf(fields, field) } });
  },
  rebaselinePicker: (baseline) => {
    const fields = get().pickerFields;
    if (fields === null) return;
    set({ pickerFields: { ...fields, baseline } });
  },
  showPickerNotice: () => {
    const fields = get().pickerFields;
    if (fields === null) return;
    set({ pickerFields: { ...fields, noticeShown: true, noticeCount: fields.noticeCount + 1 } });
  },
  openConflict: (conflict) => {
    // Opening the overlap popover replaces the picker: commit first (§5.4, path 3).
    if (get().picker !== null && !flushPicker(PickerFlushReason.Dismiss, { silent: true })) return;
    set({ conflict, picker: null, pickerFields: null, contextMenu: null });
  },
  closeConflict: () => set({ conflict: null }),
  openContextMenu: (menu) => {
    // A keyboard-opened menu may be followed by the native `contextmenu` the same key press
    // produces on some platforms: that echo must not move the menu to a pointer position.
    const current = get().contextMenu;
    if (
      menu.source === ContextMenuSource.Pointer &&
      current !== null &&
      current.source === ContextMenuSource.Keyboard &&
      Date.now() - current.openedAt < CONTEXT_MENU_ECHO_MS
    ) {
      return false;
    }
    // Opening the menu replaces the picker: commit first (§5.4); a veto means no menu (§6.3).
    if (get().picker !== null && !flushPicker(PickerFlushReason.Dismiss, { silent: true })) return false;
    set({
      contextMenu: { ...menu, openedAt: Date.now() },
      selectedKey: menu.groupKey,
      selectedExplicit: true,
      picker: null,
      pickerFields: null,
      conflict: null,
    });
    return true;
  },
  closeContextMenu: () => {
    if (get().contextMenu !== null) set({ contextMenu: null });
  },
  quickMarkActivate: (projectId, source, detail = 0) => {
    const previous = get().quickMark;
    const quickMark = activateQuickMark(previous, projectId, source, detail, Date.now());
    if (quickMark === previous) return;
    // Entering Locked through the tile's own double activation feeds the learning counter:
    // the banner's "double-click to mark continuously" hint retires after two locks (§4.4).
    if (previous.mode !== QuickMarkMode.Locked && quickMark.mode === QuickMarkMode.Locked) {
      useGestureCounts.getState().record(GestureKind.QuickLock);
    }
    set({ quickMark });
  },
  quickMarkLock: (projectId, byGesture = false) => {
    const previous = get().quickMark;
    if (byGesture && previous.mode !== QuickMarkMode.Locked) {
      useGestureCounts.getState().record(GestureKind.QuickLock);
    }
    set({ quickMark: lockQuickMark(projectId) });
  },
  quickMarkExit: () => {
    if (get().quickMark !== QUICK_MARK_OFF) set({ quickMark: QUICK_MARK_OFF });
  },
  quickMarkSettle: (outcome, startedFrom) => {
    const previous = get().quickMark;
    // Every transition replaces the state object, so identity says "unchanged since the start".
    if (previous !== startedFrom) return;
    const quickMark = settleQuickMark(previous, outcome);
    if (quickMark !== previous) set({ quickMark });
  },
  setDragSpan: (dragSpan) => {
    if (!sameSpan(get().dragSpan, dragSpan)) set({ dragSpan });
  },
  setTileDrag: (tileDrag) => {
    const current = get().tileDrag;
    if (current === tileDrag) return;
    if (
      current !== null &&
      tileDrag !== null &&
      current.date === tileDrag.date &&
      current.allowed === tileDrag.allowed &&
      sameSpan(current.span, tileDrag.span)
    ) {
      return;
    }
    set({ tileDrag });
  },
  setHoverMin: (hoverMin) => {
    if (get().hoverMin !== hoverMin) set({ hoverMin });
  },
  requestFocus: (blockId) => {
    const id = nextFocusRequestId++;
    set({ focusRequest: { id, blockId } });
    window.setTimeout(() => get().clearFocusRequest(id), FOCUS_REQUEST_MS);
  },
  clearFocusRequest: (id) => {
    if (get().focusRequest?.id === id) set({ focusRequest: null });
  },
  closeOverlays: (options) => {
    // A press outside is a Dismiss path (§5.4, path 4): commit, and show the saved toast —
    // silently on a date or view switch (path 7).
    if (
      get().picker !== null &&
      !flushPicker(PickerFlushReason.Dismiss, { silent: options?.silent === true })
    ) {
      set({ conflict: null, contextMenu: null });
      return;
    }
    set({ picker: null, pickerFields: null, conflict: null, contextMenu: null });
  },
  openHelp: () => set({ helpOpen: true }),
  closeHelp: () => set({ helpOpen: false }),
  toggleHelp: () => set({ helpOpen: !get().helpOpen }),
}));
